import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FloorplanFilters, FloorplansList, LoadingScreen, StartDesignModal } from '../../shared';
import { useFloorplanFilters } from './hooks/useFloorplanFilters';
import { useModalData } from './hooks/useModalData';
import { resetVisualizer, setActiveFloorplanId } from '../../../store/visualizerSlice';
import styles from './FloorPlansPage.module.css';

export default function FloorPlansPage() {
    const { combinedFloorplans, locations, elevations, rooms, loading, dataLoaded } = useSelector(state => state.floorplan);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const [filters, setFilters] = useState({
        location: '',
        homeType: '',
        beds: ''
    });

    const [popupState, setPopupState] = useState({
        isOpen: false,
        selectedPlan: null
    });

    const [showLocationWarning, setShowLocationWarning] = useState(false);

    // Use custom hooks for complex logic
    const { availableLocations, availableHomeTypes, availableBeds, filteredFloorplans } = 
        useFloorplanFilters(combinedFloorplans, filters);
    
    const popupData = useModalData(
        popupState.selectedPlan, 
        filters.location, 
        locations, 
        elevations, 
        rooms
    );

    // Event handlers
    const handleSelectPlan = (plan) => {
        if (!filters.location) {
            setShowLocationWarning(true);
            return;
        }
        
        setShowLocationWarning(false);
        setPopupState({ isOpen: true, selectedPlan: plan });
    };

    const handleClosePopup = () => {
        setPopupState({ isOpen: false, selectedPlan: null });
    };

    const handleStartDesigning = (selections) => {
        console.log('Starting design with:', { plan: popupState.selectedPlan, ...selections });
        
        // Reset visualizer state to clear any previous selections
        dispatch(resetVisualizer());
        console.log('Visualizer state reset - starting fresh');
        
        // Navigate to visualizer page with URL params
        const floorplanId = popupState.selectedPlan?.id || popupState.selectedPlan?.planName;
        const elevationId = selections.elevation?.id;
        
        dispatch(setActiveFloorplanId(floorplanId));
        navigate(`/visualizer/${floorplanId}/${elevationId}`, {
            state: {
                floorplan: popupState.selectedPlan,
                location: filters.location,
                community: selections.community,
                elevation: selections.elevation,
                scene: selections.scene
            }
        });
        
        handleClosePopup();
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
        setShowLocationWarning(false);
    };

    const handleClearFilters = () => {
        setFilters({ location: '', homeType: '', beds: '' });
    };

    // Loading state
    if (loading || !dataLoaded) {
        return <LoadingScreen isOpen={true} />;
    }

    // No data state
    if (combinedFloorplans.length === 0) {
        return (
            <section className={styles.container}>
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <h2>No floorplan data available</h2>
                    <p>Please check the browser console for error messages.</p>
                </div>
            </section>
        );
    }

    return (
        <section className={styles.container}>
            <FloorplanFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                availableLocations={availableLocations}
                availableHomeTypes={availableHomeTypes}
                availableBeds={availableBeds}
            />
            
            {showLocationWarning && (
                <div className={styles.warningMessage}>
                    <span className={styles.warningIcon}>⚠</span>
                    <span className={styles.warningText}>
                        Please select a location from the filters above before choosing a floor plan.
                    </span>
                    <button 
                        className={styles.warningClose}
                        onClick={() => setShowLocationWarning(false)}
                        aria-label="Close warning"
                    >
                        ×
                    </button>
                </div>
            )}
            
            <div className={styles.scrollableContent}>
                <FloorplansList 
                    floorplans={filteredFloorplans} 
                    onSelectPlan={handleSelectPlan} 
                />
            </div>

            <StartDesignModal
                isOpen={popupState.isOpen}
                onClose={handleClosePopup}
                floorplanName={popupState.selectedPlan?.planName || ''}
                communities={popupData.communities}
                elevations={popupData.elevations}
                scenes={popupData.scenes}
                onStartDesigning={handleStartDesigning}
            />
        </section>
    );
}
